import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Button, Card, CardContent, InputAdornment, TextField, alpha, useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import CustomContactTable from '../../components/CustomContactTable';
import ContactFormModal from '../../components/Forms/ContactFormModal';
import ContactFormFields from '../../components/Forms/ContactFormFields';
import ContactRowActions from '../../components/Forms/ContactRowActions';
import { contactCreate } from '../../redux/actions';
import api from '../../utils/functions/api';
import Error from '../../components/Error';
import { validateThaiAddress } from '../../utils/functions/thaiZipCascade';
import useZipCascade from '../../hooks/useZipCascade';
import useContactList from '../../hooks/useContactList';
import { confirmAsync } from '../../utils/functions/globalDialog';
import { emptyValues } from './client.constants';

function Client() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    contact, localRows, setLocalRows, inputValue, setInputValue, size, setSize, page, setPage, refetch,
  } = useContactList('client');

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const [addSubmitError, setAddSubmitError] = useState('');
  const [editSubmitError, setEditSubmitError] = useState('');

  const {
    handleSubmit: handleAddSubmit, control: addControl, reset: addReset, setValue: addSetValue, watch: addWatch,
  } = useForm({ defaultValues: emptyValues });
  const {
    handleSubmit: handleEditSubmit, control: editControl, reset: editReset, setValue: editSetValue, watch: editWatch,
  } = useForm({ defaultValues: emptyValues });

  const { cascade: addCascade, setZipSelections: setAddZipSelections } = useZipCascade(addWatch, addSetValue);
  const { cascade: editCascade, setZipSelections: setEditZipSelections } = useZipCascade(editWatch, editSetValue);

  const handleAddOpen = () => {
    setAddZipSelections({ province: null, district: null });
    setAddSubmitError('');
    setAddOpen(true);
  };
  const handleAddClose = () => { setAddOpen(false); addReset(emptyValues); };

  const handleEditOpen = (row) => {
    setEditingRow(row);
    editReset(row);
    // seed จากค่าที่มีอยู่เดิม ไม่งั้น cascade จะ recompute แล้วเคลียร์อำเภอ/ตำบลที่ถูกต้องอยู่แล้วทิ้ง
    setEditZipSelections({ province: row?.province || null, district: row?.amphoe || null });
    setEditSubmitError('');
    setEditOpen(true);
  };
  const handleEditClose = () => { setEditOpen(false); setEditingRow(null); editReset(emptyValues); };

  const onAddSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) { setAddSubmitError(zipError); return; }
    try {
      await dispatch(contactCreate({ ...data, role: 'client' }));
      refetch();
      handleAddClose();
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const onEditSubmit = async (data) => {
    const zipError = validateThaiAddress(data);
    if (zipError) { setEditSubmitError(zipError); return; }
    const prevRows = localRows;
    setLocalRows((prev) => prev.map((r) => (r._id === editingRow._id ? { ...r, ...data } : r)));
    handleEditClose();
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/contact/${editingRow._id}`, data);
    } catch (err) {
      setLocalRows(prevRows);
      console.error('Failed to update client:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmAsync('ยืนยันการลบ?'))) return;
    const prevRows = localRows;
    setLocalRows((prev) => prev.filter((r) => r._id !== id));
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/contact/${id}`);
    } catch (err) {
      setLocalRows(prevRows);
      console.error('Failed to delete client:', err);
    }
  };

  if (!contact.isLoading && !contact.isCompleted) {
    return <Error />;
  }

  return (
    <Box>
      <ContactFormModal
        open={addOpen}
        onClose={handleAddClose}
        title="เพิ่มลูกค้าใหม่"
        subtitle="กรอกข้อมูลลูกค้าองค์กรหรือบุคคลธรรมดา"
        onSubmit={handleAddSubmit(onAddSubmit)}
        error={addSubmitError}
      >
        <ContactFormFields control={addControl} cascade={addCascade} setZipSelections={setAddZipSelections} />
      </ContactFormModal>

      <ContactFormModal
        open={editOpen}
        onClose={handleEditClose}
        title="แก้ไขข้อมูลลูกค้า"
        subtitle={editingRow?.name || ''}
        onSubmit={handleEditSubmit(onEditSubmit)}
        error={editSubmitError}
      >
        <ContactFormFields control={editControl} cascade={editCascade} setZipSelections={setEditZipSelections} />
      </ContactFormModal>

      <Card elevation={0} sx={{ mb: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.15)}` }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
            <TextField
              label="ค้นหาลูกค้า"
              placeholder="ชื่อบริษัท, ผู้ติดต่อ, อีเมล..."
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: '350px' }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-end' } }}>
              <Button variant="contained" onClick={handleAddOpen} color="primary" startIcon={<AddIcon />} sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}>
                เพิ่มลูกค้า
              </Button>
              <Button variant="outlined" color="primary" startIcon={<CloudUploadIcon />} sx={{ borderRadius: 2, textTransform: 'none', px: 2 }}>
                Upload CSV
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <CustomContactTable
        rows={localRows}
        page={page}
        size={size}
        total={contact.total || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(e) => { setSize(e.target.value); setPage(1); }}
        actionCell={(row) => <ContactRowActions row={row} onEdit={handleEditOpen} onDelete={handleDelete} />}
        isLoading={contact.isLoading}
      />
    </Box>
  );
}

export default Client;
